import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAssetMetricsQuery } from '@src/providers/CoinMetricsApiProvider';
import type { SeriesLineOptions } from 'highcharts';
import { useMemo } from 'react';
import { CM_FETCH_LIMIT_PER_ASSET, CM_MAX_PAGE_SIZE, SIGNALS_TIME_RANGE_LIMIT, type SignalsTimeRange } from '../core';
import type { AggregationMethod } from './AggregatedMetric.types';
import { aggregate, groupByAsset, type TimeSeriesPoint } from './aggregation';

interface UseAggregatedMetricDataParams {
  /** CoinMetrics metric to fetch (e.g. `'CapMrktEstUSD'`). */
  metric: string;
  /** CoinMetrics frequency parameter (e.g. `'1d'`). */
  frequency: string;
  /** Used as the resulting series' `name` — surfaces in chart legends and tooltips. */
  seriesLabel: string;
  selectedCmAssetIds: string[];
  aggregationMethod: AggregationMethod;
  timeRange: SignalsTimeRange;
}

interface UseAggregatedMetricDataResult {
  /** Single-series array ready to pass straight to `SignalsLineChart`. */
  aggregatedSeries: SeriesLineOptions[];
  isFetching: boolean;
  isError: boolean;
  /**
   * True when the response hit the pagination cap with more data available — the
   * aggregated series is then incomplete. Callers should surface this rather than
   * charting a silently-partial line.
   */
  isTruncated: boolean;
}

const EMPTY_SERIES: SeriesLineOptions[] = [];
const EMPTY_DATA_BY_ASSET: Map<string, TimeSeriesPoint[]> = new Map();

/**
 * Fetches the configured metric for every selected asset in one CoinMetrics call,
 * groups the response by asset, then aggregates client-side. Time range slicing
 * also happens client-side so switching ranges is instant.
 */
export function useAggregatedMetricData({
  metric,
  frequency,
  seriesLabel,
  selectedCmAssetIds,
  aggregationMethod,
  timeRange,
}: UseAggregatedMetricDataParams): UseAggregatedMetricDataResult {
  // Sort before joining so ['btc','eth'] and ['eth','btc'] hit the same RTK Query cache entry
  // and a new array reference with identical contents doesn't trigger a duplicate fetch.
  const assetsParam = useMemo(() => [...selectedCmAssetIds].toSorted().join(','), [selectedCmAssetIds]);

  // `skipToken` short-circuits the query entirely when nothing is selected — no network call,
  // no error state, hook just returns empty results.
  const queryArg =
    selectedCmAssetIds.length === 0
      ? skipToken
      : {
          assets: assetsParam,
          metrics: metric,
          frequency,
          limit_per_asset: CM_FETCH_LIMIT_PER_ASSET,
          page_size: CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  // `currentData` (not `data`) so stale results stay visible during a re-fetch — the chart
  // doesn't blank out while the user is editing selections.
  const { currentData, isFetching, isError } = useGetAssetMetricsQuery(queryArg, {
    selectFromResult: ({ currentData, isFetching, isError }) => ({ currentData, isFetching, isError }),
  });

  const isTruncated = currentData?.truncated === true;

  const dataByAsset = useMemo(
    () => (currentData?.data ? groupByAsset(currentData.data, metric) : EMPTY_DATA_BY_ASSET),
    [currentData, metric]
  );

  const aggregatedSeries = useMemo<SeriesLineOptions[]>(() => {
    if (selectedCmAssetIds.length === 0) {
      return EMPTY_SERIES;
    }
    const aggregated = aggregate(dataByAsset, selectedCmAssetIds, aggregationMethod);
    const sliced = sliceToTimeRange(aggregated, timeRange);
    return [{ type: 'line', name: seriesLabel, data: sliced } satisfies SeriesLineOptions];
  }, [dataByAsset, selectedCmAssetIds, aggregationMethod, timeRange, seriesLabel]);

  return { aggregatedSeries, isFetching, isError, isTruncated };
}

/** Keeps only the trailing N days for the active time range. `null` (the `All` range) returns the input unchanged. */
function sliceToTimeRange(series: TimeSeriesPoint[], timeRange: SignalsTimeRange): TimeSeriesPoint[] {
  const limit = SIGNALS_TIME_RANGE_LIMIT[timeRange];
  if (limit == null || series.length <= limit) {
    return series;
  }
  return series.slice(series.length - limit);
}
